import { useState, FC } from "react";
import { createModalConfig, ModalConfig } from "../../../Models/ModalConfig";

import {
  DEFAULT_HANDLER_CONFIG,
  AddressBookIndexContext,
  AddressBookIndexContextValue,
  AddressBookIndexProviderProps,
} from "./";

/* ===========================================================================
 * PROVIDER: AddressBookIndexProvider
 * =========================================================================== */

/**
 * @component AddressBookIndexProvider
 *
 * Provides configuration and UI state for the AddressBook index view.
 *
 * Responsibilities:
 * - Centralizes handler configuration (registry, billing, defaults).
 * - Manages Modal state and UI visibility.
 * - Aggregates owner and service metadata for sub-components.
 * - Provides a unified context for listing and filtering records.
 *
 * @param {AddressBookIndexProviderProps} props - Provider configuration.
 * @returns {JSX.Element} The wrapped children with context applied.
 */
export const AddressBookIndexProvider: FC<AddressBookIndexProviderProps> = ({
  service = "registry",
  ownerId,
  myAddress = false,
  children,
  callback,
  isBilling = false,
  handlerConfig: incomingHandlerConfig,
}) => {
  /* ---------------------------------------------------------------------------
   * CONFIGURATION & STATE
   * ------------------------------------------------------------------------- */

  /** * Merged configuration for the address book handlers.
   * Ensures fallback to DEFAULT_HANDLER_CONFIG if props are missing.
   */
  const handlerConfig = incomingHandlerConfig
    ? { ...DEFAULT_HANDLER_CONFIG, ...incomingHandlerConfig }
    : DEFAULT_HANDLER_CONFIG;

  /** * Modal configuration state.
   * Controls opening, closing and data payload for internal modals.
   */
  const [modalConfig, setModalConfig] =
    useState<ModalConfig>(createModalConfig());

  /* ---------------------------------------------------------------------------
   * CONTEXT VALUE AGGREGATION
   * ------------------------------------------------------------------------- */

  /**
   * Final context object exposed to consumers.
   */
  const contextValue: AddressBookIndexContextValue = {
    service,
    ownerId,
    myAddress,
    ...handlerConfig,
    modalConfig,
    isBilling,
    setModalConfig,
    callback,
  };

  /* ---------------------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------------------- */

  return (
    <AddressBookIndexContext.Provider value={contextValue}>
      {children}
    </AddressBookIndexContext.Provider>
  );
};
